/**
 * Filter helpers for the assessment family of list pages. The KPI
 * cascade filter lives on the KPI picker modal — see KpiPickerModal.js
 * — because assessments themselves don't carry tags; only KPIs do.
 */

/**
 * Turn a raw enum-ish string ("IN_PROGRESS", "draft", "RIU_TEMPLATE")
 * into a human label ("In progress", "Draft", "Riu template"). Used as
 * the label for every status / owner option we surface, so no per-page
 * hardcoded map.
 */
function humaniseStatus(value) {
  if (value == null || value === "") return String(value ?? "");
  return String(value)
    .toLowerCase()
    .replace(/_+/g, " ")
    .replace(/^\s*([a-z])/, (m, c) => c.toUpperCase());
}

/**
 * Walk the rows, collect every distinct value at `row[field]`, and
 * return them as `[{ value, label }, ...]` sorted alphabetically.
 */
export function buildStatusOptions(rows, field = "status_key") {
  const seen = new Set();
  for (const r of rows || []) {
    const v = r?.[field];
    if (v == null || v === "") continue;
    seen.add(String(v));
  }
  return Array.from(seen)
    .sort()
    .map((value) => ({ value, label: humaniseStatus(value) }));
}

/**
 * Build `{ value, label }[]` options from rows where each row holds a
 * value at `idField` and the human label resolves through `labelMap`.
 * Used by the industry / sub-industry filters on AllAssessments.
 */
export function buildLookupOptions(rows, idField, labelMap = {}) {
  const seen = new Set();
  for (const r of rows || []) {
    const v = r?.[idField];
    if (v == null || v === "") continue;
    seen.add(String(v));
  }
  return Array.from(seen)
    .map((value) => ({
      value,
      label: labelMap[value] || labelMap[Number(value)] || `#${value}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function rowMatchesSearch(row, search, extras = {}) {
  const q = (search || "").trim().toLowerCase();
  if (!q) return true;
  const haystacks = [
    row?.title,
    row?.assessment_title,
    row?.company_name,
    row?.supplier_name,
    extras?.assessmentTitle,
    extras?.supplierName,
  ];
  return haystacks.filter(Boolean).some((s) =>
    String(s).toLowerCase().includes(q)
  );
}

function rowMatchesIn(row, values, field) {
  if (!values?.length) return true;
  const v = row?.[field];
  if (v == null) return false;
  return values.includes(String(v));
}

function withinRange(dateStr, from, to) {
  if (!dateStr) return false;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return false;
  if (from) {
    const fromT = new Date(from).getTime();
    if (Number.isFinite(fromT) && t < fromT) return false;
  }
  if (to) {
    const toT = new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1;
    if (Number.isFinite(toT) && t > toT) return false;
  }
  return true;
}

function rowMatchesDate(row, dateRange, dateField) {
  if (!dateRange?.from && !dateRange?.to) return true;
  if (!dateField) return true;
  return withinRange(row?.[dateField], dateRange.from, dateRange.to);
}

/**
 * Apply the standard filter set + any per-page multi-value filters
 * passed under `lookups`. Pure — returns a new array; never mutates.
 *
 * @param {object} opts
 * @param {string} opts.search
 * @param {string[]} opts.statusFilter
 * @param {string} opts.statusField - default "status_key"
 * @param {object} opts.dateRange - { from, to }
 * @param {string} opts.dateField - default "created_at"
 * @param {Array<{ values: string[], field: string }>} opts.lookups
 * @param {(row) => object} opts.rowExtras
 */
export function applyAssessmentFilters(rows, opts = {}) {
  const {
    search = "",
    statusFilter = [],
    statusField = "status_key",
    dateRange = { from: "", to: "" },
    dateField = "created_at",
    lookups = [],
    rowExtras,
  } = opts;
  return (rows || []).filter((r) => {
    const extras = rowExtras ? rowExtras(r) : undefined;
    if (!rowMatchesSearch(r, search, extras)) return false;
    if (!rowMatchesIn(r, statusFilter, statusField)) return false;
    if (!rowMatchesDate(r, dateRange, dateField)) return false;
    for (const lk of lookups) {
      if (!rowMatchesIn(r, lk.values, lk.field)) return false;
    }
    return true;
  });
}
